import {
  AccountingAccount,
  AccountType,
} from "@/apps/tatar/accounting/interfaces/account.interface";
import { APDunningActivity } from "@/apps/tatar/activityApp/specific/dunning/dunning.interface";
import { ObjectKind } from "@/apps/tatar/objectsApp/types/objectKind.interface";
import AssetLoader from "@/components/AssetLoader/AssetLoader";
import StructLoader from "@/components/StructLoader/StructLoader";
import BFCheckbox from "@/modules/abstract-ui/forms/checkbox/BFCheckbox";
import DunningStruct, {
  DunningStructClass,
} from "@/redux/actions/struct/implemented/DunningStruct";
import ObjectKindStruct from "@/redux/actions/struct/implemented/ObjectKindStruct";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import UnitStruct from "@/redux/actions/struct/implemented/UnitStruct";
import CacheService from "@/services/CacheService";
import DataBusDefaults from "@/services/DataBusDefaults";
import CSSUtils from "@/utils/CSSUtils";
import _ from "lodash";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import ModalManager from "../../../../../../../components/ModalComponent/ModalManager";
import InfiniteTableFilter, {
  FilterComponentInitialState,
} from "../../../../../../../configurable/data/FilterComponent/InfiniteTableFilter";
import VirtualizedTableExportButton from "../../../../../../../configurable/data/VirtualizedTable/VirtualizedTableExportButton";
import TablePage from "../../../../../../../configurable/layouts/TablePageLayout/TablePage";
import DebugDataComponent from "../../../../../../../debug/DebugDataComponent";
import i18n from "../../../../../../../i18n";
import { AssetTypes } from "../../../../../../../model/AssetTypes";
import BFStatus from "../../../../../../../modules/abstract-ui/data/status/BFStatus";
import {
  AssetCell,
  LinkCell,
  renderCellValue,
} from "../../../../../../../modules/abstract-ui/data/table/TableUtils";
import { ColumnConfig } from "../../../../../../../modules/abstract-ui/data/virtualized-table/BFVirtualizedTable";
import BFSelect from "../../../../../../../modules/abstract-ui/forms/select/BFSelect";
import BFButton from "../../../../../../../modules/abstract-ui/general/Button/BFButton";
import { DocumentStoreAssetParams } from "../../../../../../../modules/document-store/DSInterfaces";
import DSService from "../../../../../../../modules/document-store/DSService";
import DSUnmentRequirementsColumn from "../../../../../../../modules/document-store/components/DSUnmetRequirementsColumn/DSUnmetRequirementsColumn";
import {
  getDefaultAreaNumberFormat,
  getDefaultCurrencyNumberFormat,
} from "../../../../../../../modules/export/export.model";
import { setFlexCacheData } from "../../../../../../../redux/actions/application/application-actions";
import { selectFlexConfig } from "../../../../../../../redux/actions/application/application-selectors";
import {
  useAggregationTableQuery,
  useTypedSelector,
} from "../../../../../../../redux/hooks";
import { AggregationStatisticQuerySelector } from "../../../../../../../redux/model";
import DataBus from "../../../../../../../services/DataBus";
import LanguageService from "../../../../../../../services/LanguageService";
import PermissionService from "../../../../../../../services/PermissionService";
import { DataBusSubKeys } from "../../../../../../../utils/Constants";
import MQ from "../../../../../../../utils/MatchQueryUtils";
import StringUtils from "../../../../../../../utils/StringUtils";
import CBRentalUtils from "../../CBRentalUtils";
import {
  CB_RENTAL_AGREEMENT_LIST,
  getConfigRentalUnitTypeGroup,
} from "../../CBTenantsConst";
import { RentalAdjustment, RentalAgreement } from "../../TenantsInterfaces";
import { CBRentalAgreementsFilterOptions } from "./CBRentalAgreementsFilterOptions";
import "./CBRentalAgreementsList.scss";
import CBRentalAgreementFormWizard from "./rental-agreement-form-wizard/CBRentalAgreementFormWizard";

export const RENTAL_AGREEMENT_STATIC_SELECTORS: AggregationStatisticQuerySelector[] =
  [
    {
      name: "general",
      op: [
        { key: "count", op: "count" },
        {
          key: "deposit",
          op: "sum",
          field: "data.deposit.depositValue",
        },
        {
          key: "balance",
          op: "sum",
          field: "data.balance",
        },
        {
          key: "rentGross",
          op: "sum",
          field: "data.rentGross",
        },
        {
          key: "rentNet",
          op: "sum",
          field: "data.rentNet",
        },
        {
          key: "operatingCostGross",
          op: "sum",
          field: "data.operatingCostGross",
        },
        {
          key: "operatingCostNet",
          op: "sum",
          field: "data.operatingCostNet",
        },
      ],
      query: {},
    },
    {
      name: "positiveBalance",
      op: [
        {
          key: "balance",
          op: "sum",
          field: "data.balance",
        },
      ],
      query: {
        matchQuery: MQ.lt("data.balance", 0),
      },
    },
    {
      name: "negativeBalance",
      op: [
        {
          key: "balance",
          op: "sum",
          field: "data.balance",
        },
      ],
      query: {
        matchQuery: MQ.gt("data.balance", 0),
      },
    },
  ];

const filterData = () => {
  return [
    {
      label: i18n.t("cb:RentalAgreement.FilterBy.all", "Alle Mietverträge"),
      value: "all",
      query: null,
    },
    {
      label: i18n.t(
        "cb:RentalAgreement.FilterBy.active",
        "Aktuelle Mietverträge"
      ),
      value: "active",
      query: MQ.and(
        MQ.lte("data.moveIn", moment().startOf("day")),
        MQ.or(
          MQ.gte("data.moveOut", moment().startOf("day")),
          MQ.isNull("data.moveOut")
        )
      ),
    },
    {
      label: i18n.t(
        "cb:RentalAgreement.FilterBy.inactive",
        "Abgelaufene Mietverträge"
      ),
      value: "inactive",
      query: MQ.and(MQ.lte("data.moveOut", moment().endOf("day"))),
    },
    {
      label: i18n.t(
        "cb:RentalAgreement.FilterBy.future",
        "Zukünftige Mietverträge"
      ),
      value: "future",
      query: MQ.and(MQ.gt("data.moveIn", moment().startOf("day"))),
    },
  ];
};
const FlexKey = "cb_rentalAgreementList";
interface RentersViewProps {
  kind: ObjectKind;
}
const CBRentalAgreementsList = (props: RentersViewProps) => {
  const dispatch = useDispatch();
  const [initialState, setInitialState] =
    useState<FilterComponentInitialState>(undefined);
  const aggregated = useAggregationTableQuery(
    CB_RENTAL_AGREEMENT_LIST,
    RENTAL_AGREEMENT_STATIC_SELECTORS
  );
  const { filterValue, filterDate } = useTypedSelector((state) =>
    selectFlexConfig(state, FlexKey)
  ) || { filterValue: "active", filterDate: new Date() };

  const filterOptions = filterData();
  const additionalMatchQuery = filterOptions.find(
    (e) => e.value === filterValue
  )?.query;
  return (
    <TablePage
      layout={{
        className: "cb-rental-agreements-list",
        cardHeader: (
          <div className={`agreements-header`}>
            <div className={`margin-bottom-10 __h1 `}>
              {i18n.t("cb:RentalAgreement.title", "Mietverträge")}
            </div>
            <div className={`__flex`}>
              <BFSelect
                onChange={(value) => {
                  dispatch(setFlexCacheData(FlexKey, "filterValue", value));
                }}
                cleanable={false}
                value={filterValue}
                data={filterOptions.map((e) => ({
                  label: e.label,
                  value: e.value,
                }))}
              />
            </div>
            <VirtualizedTableExportButton
              maxData={2500}
              identifier={CB_RENTAL_AGREEMENT_LIST}
            />
          </div>
        ),
        pageHeader: (
          <>
            <InfiniteTableFilter
              initialState={initialState}
              identifier={CB_RENTAL_AGREEMENT_LIST}
              filterOptions={CBRentalAgreementsFilterOptions(props.kind)}
            />
            {PermissionService.hasObjectKindPermission(
              props.kind,
              "rental.agreements.createEdit"
            ) && (
              <BFButton
                onClick={() => {
                  ModalManager.show({
                    backdrop: "static",
                    size: "fluid",
                    noPadding: true,
                    content: (state, setState, onClose) => (
                      <CBRentalAgreementFormWizard
                        kind={props.kind}
                        onClose={onClose}
                        onSuccess={(agreement) => {
                          DataBusDefaults.route({
                            append: true,
                            route: `${agreement._id}/accounting`,
                          });
                        }}
                      />
                    ),
                  });
                }}
              >
                {i18n.t("cb:RentalAgreement.add", "Mietvertrag hinzufügen")}
              </BFButton>
            )}
          </>
        ),
      }}
      table={{
        onRowDoubleClick: (asset: RentalAgreement, index) => {
          DataBus.emit(DataBusSubKeys.ROUTE, {
            append: true,
            route: `${asset._id}`,
          });
        },
        additionalMatchQuery: MQ.and(additionalMatchQuery),
        identifier: CB_RENTAL_AGREEMENT_LIST,
        dataUrl: `/api/asset/list/${AssetTypes.Rental.RentalAgreement}`,
        hover: true,
        expandKeys: ["data.rentalUnits", "data.tenant"],
        selection: "single",
        asPost: true,
        onRowClick: (node: RentalAgreement) => {},
        params: { aggregated },
        exportOptions: {
          filename: `rental-agreements_${moment().format("YYYY-MM-DD")}`,
        },
        columns: {
          "data.type": {
            label: i18n.t("Base.Unit"),
            sortable: true,
            flexWidth: 120,
            resizableOptions: {
              min: 40,
              max: 200,
            },
            resizable: true,
            render: (node: RentalAgreement, index, params) =>
              renderCellValue(
                node?.data?.type,
                "-",
                (value: string) => UnitStruct.getUnit(value)?.data.label
              ),
            renderFooter: (params) =>
              renderCellValue(
                `${i18n.t("cb:RentalAgreement.count", "Anz.")}: ${
                  params?.aggregated?.data?.["general"]?.count || "-"
                }`
              ),
            export: {
              width: 20,
              label: i18n.t("Base.Unit"),
              type: "string",
              selector: (node: RentalAgreement) =>
                LanguageService.translateLabel(
                  UnitStruct.getUnit(node?.data?.type)?.data.label
                ),
            },
          },
          "nested.entityName": {
            label: i18n.t("Base.Entity"),
            // sortKey: "",
            flexWidth: 120,
            sortable: true,
            resizable: true,
            render: (node: RentalAgreement, index, params) =>
              renderCellValue(node?.nested?.entityName, "-"),
            export: {
              width: 20,
              label: i18n.t("Base.Entity"),
              type: "string",
              selector: (node: RentalAgreement) => node?.nested?.entityName,
            },
          },
          "nested.objectNumber": {
            label: i18n.t("Base.Object"),
            flexWidth: 200,
            resizable: true,
            sortable: true,
            render: (node: RentalAgreement, index, params) => (
              <LinkCell
                assetType={AssetTypes.Portfolio.Object}
                id={node?.data.objectId}
                text={`${node?.nested?.objectNumber} - ${node?.nested?.objectName}`}
                type={node?.data.type}
              />
            ),
            export: {
              width: 20,
              label: i18n.t("Base.Object"),
              type: "string",
              selector: (node: RentalAgreement) =>
                `${node?.nested?.objectNumber} - ${node?.nested?.objectName}`,
            },
          },
          ...RENTAL_AGREEMENT_FIELDS(),
        },
      }}
    />
  );
};

export default CBRentalAgreementsList;

export const RENTAL_AGREEMENT_FIELDS = (
  withObject = false,
  withEntity = false
) =>
  ({
    ...(withEntity
      ? {
          "data.entity": {
            label: i18n.t("cb:RentalVacancy.entity", "Gesellschaft"),
            // sortKey: "",
            flexWidth: 240,
            sortable: true,
            resizable: true,
            render: (node: RentalAgreement, index, params) => (
              <>
                {renderCellValue(
                  node?.data?.entity,
                  "-",
                  (id: string) =>
                    `${OrgaStruct.getEntity(id)?.id} - ${
                      OrgaStruct.getEntity(id)?.displayName
                    }`
                )}
              </>
            ),
            export: {
              width: 20,
              label: i18n.t("cb:RentalVacancy.entity", "Gesellschaft"),
              type: "string",
              selector: (node: RentalAgreement) =>
                node?.data?.objectId
                  ? `${OrgaStruct.getEntity(node?.data?.objectId)?.id} - ${
                      OrgaStruct.getEntity(node?.data?.objectId)?.displayName
                    }`
                  : "-",
            },
          },
        }
      : {}),
    ...(withObject
      ? {
          "data.objectId": {
            label: i18n.t("cb:RentalVacancy.object", "Objekt"),
            // sortKey: "",
            flexWidth: 120,
            sortable: true,
            resizable: true,
            render: (node: RentalAgreement, index, params) => (
              <>
                {renderCellValue(
                  node?.data?.objectId,
                  "-",
                  (id: string) =>
                    `${OrgaStruct.getObject(id)?.id} - ${
                      OrgaStruct.getObject(id)?.displayName
                    }`
                )}
              </>
            ),
            export: {
              width: 20,
              label: i18n.t("cb:RentalVacancy.object", "Objekt"),
              type: "string",
              selector: (node: RentalAgreement) =>
                node?.data?.objectId
                  ? `${OrgaStruct.getObject(node?.data?.objectId)?.id} - ${
                      OrgaStruct.getObject(node?.data?.objectId)?.displayName
                    }`
                  : "-",
            },
          },
        }
      : {}),
    "data.id": {
      label: i18n.t("cb:RentalAgreement.id", "ID"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <>
          <DebugDataComponent data={node} />
          {renderCellValue(node?.data?.id, "-")}
        </>
      ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.id", "ID"),
        type: "string",
        selector: (node: RentalAgreement) => node?.data?.id,
      },
    },

    "expand.tenant.displayName": {
      label: i18n.t("cb:RentalAgreement.tenant", "Mieter"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <LinkCell
          assetType={AssetTypes.Contact}
          id={node?.data.tenant}
          text={
            node?.expand?.tenant?.data.displayName ||
            node?.nested?.tenantName ||
            node?.data.tenant
          }
          type={node?.data.type}
        />
      ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.tenant", "Mieter"),
        type: "string",
        selector: (node: RentalAgreement) =>
          node?.nested?.tenantName || node?.data.tenant,
      },
      // renderCellValue(node?.nested?.tenantDisplayName, "-"),
    },

    "data.displayName": {
      label: i18n.t("cb:RentalAgreement.displayName", "Name"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.displayName, "-"),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.displayName", "Name"),
        type: "string",
        selector: (node: RentalAgreement) => node?.data?.displayName,
      },
    },

    internalId: {
      label: i18n.t(
        "cb:RentalAgreement.accountingInternalId",
        "Interne Kontonummer"
      ),
      // sortKey: "",
      flexWidth: 120,
      sortable: false,
      resizable: true,
      hidden: true,
      render: (node: RentalAgreement, index, params) => (
        <AssetLoader
          assetType={AssetTypes.Accounting.Account}
          query={MQ.and(
            MQ.eq("data.accountType", AccountType.debitor_rentalagreement),
            MQ.eq("data.linkedAsset.assetId", node._id)
          )}
          render={(account: AccountingAccount) =>
            renderCellValue(account?.data.internalId, "-")
          }
        />
      ),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.accountingInternalId",
          "Interne Kontonummer"
        ),
        type: "string",
        selector: async (node: RentalAgreement) => {
          const account = await CacheService.getData({
            oType: "asset",
            assetType: AssetTypes.Accounting.Account,
            query: MQ.and(
              MQ.eq("data.accountType", AccountType.debitor_rentalagreement),
              MQ.eq("data.linkedAsset.assetId", node._id)
            ),
            checkTables: true,
          });
          if (account) {
            return account.data.internalId;
          }
          return "";
        },
      },
      // renderCellValue(node?.nested?.tenantDisplayName, "-"),
    },
    externalId: {
      label: i18n.t(
        "cb:RentalAgreement.accountingExternalId",
        "Fremdsystem Kontonummer"
      ),
      // sortKey: "",
      flexWidth: 120,
      sortable: false,
      resizable: true,
      hidden: true,
      render: (node: RentalAgreement, index, params) => (
        <AssetLoader
          assetType={AssetTypes.Accounting.Account}
          query={MQ.and(
            MQ.eq("data.accountType", AccountType.debitor_rentalagreement),
            MQ.eq("data.linkedAsset.assetId", node._id)
          )}
          render={(account: AccountingAccount) =>
            renderCellValue(account?.data.exportId, "-")
          }
        />
      ),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.accountingExternalId",
          "Fremdsystem Kontonummer"
        ),
        type: "string",
        selector: async (node: RentalAgreement) => {
          const account = await CacheService.getData({
            oType: "asset",
            assetType: AssetTypes.Accounting.Account,
            query: MQ.and(
              MQ.eq("data.accountType", AccountType.debitor_rentalagreement),
              MQ.eq("data.linkedAsset.assetId", node._id)
            ),
            checkTables: true,
          });
          if (account) {
            return account.data.exportId;
          }
          return "";
        },
      },
    },

    rentAdjustments: {
      label: i18n.t("cb:RentalAgreement.rentAdjustments", "Mietanpassungen"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <AssetLoader
          assetType={AssetTypes.Rental.RentalAdjustment}
          query={MQ.and(
            MQ.eq("data.rentalAgreementId", node._id),
            MQ.eq("data.status", "active"),
            MQ.gte("data.from", moment().startOf("day").toDate()),
            MQ.or(
              MQ.isNull("data.to"),
              MQ.lte("data.to", moment().endOf("day").toDate())
            )
          )}
          renderError={() => null}
          render={(adjustment: RentalAdjustment) =>
            adjustment ? (
              <div
                className={`unit-type-group-container`}
                style={{ maxWidth: "100%", overflow: "hidden" }}
              >
                {!!adjustment.data.rentNet && (
                  <BFStatus
                    size="xs"
                    color="rgb(139,26,26)"
                    label={
                      StringUtils.formatPercent(
                        adjustment.data.rentNet / node.data.rentNet,
                        true
                      ) +
                      " " +
                      i18n.t(
                        "cb:RentalAgreement.rentAdjustment.RentNet",
                        "Miete"
                      )
                    }
                  />
                )}
                {!!adjustment.data.operatingCostsNet && (
                  <BFStatus
                    size="xs"
                    color="rgb(139,26,26)"
                    label={
                      StringUtils.formatPercent(
                        adjustment.data.operatingCostsNet /
                          node.data.operatingCostNet,
                        true
                      ) +
                      " " +
                      i18n.t(
                        "cb:RentalAgreement.rentAdjustment.OperatingCost",
                        "Nebenkosten"
                      )
                    }
                  />
                )}
              </div>
            ) : null
          }
        />
      ),
    },

    "data.contractType": {
      label: i18n.t("cb:RentalAgreement.contractType", "Vertragsart"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.contractType, "-", (value) =>
          CBRentalUtils.getContractTypeLabel(value)
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.contractType", "Vertragsart"),
        type: "string",
        selector: (node: RentalAgreement) => node?.data?.contractType,
      },
    },

    "expand.rentalUnits.data.unitTypeGroup": {
      label: i18n.t("cb:RentalUnit.unitTypeGroup", "Nutzart"),
      // sortKey: "",
      flexWidth: 200,
      sortable: false,
      resizable: true,
      render: (node: RentalAgreement, index, params) => {
        return (
          <div
            className={`unit-type-group-container`}
            style={{ maxWidth: "100%", overflow: "hidden" }}
          >
            {_.uniq(
              node.expand?.rentalUnits
                ?.map((e) => e.data.unitType)
                ?.map(
                  (unitType) => ObjectKindStruct.getUnitTypeBy(unitType)?.group
                )
                .filter((e) => e)
            ).map((group) => {
              const status = getConfigRentalUnitTypeGroup(group);
              if (status) {
                return (
                  <div style={{ maxWidth: "100%", overflow: "hidden" }}>
                    <BFStatus
                      size="sm"
                      color={status.color}
                      label={status.label}
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      },
      export: {
        width: 20,
        label: i18n.t("cb:RentalUnit.unitTypeGroup", "Nutzart"),
        type: "string",
        selector: (node: RentalAgreement) =>
          _.uniq(
            node.expand?.rentalUnits
              ?.map((e) => e.data.unitType)
              ?.map(
                (unitType) => ObjectKindStruct.getUnitTypeBy(unitType)?.group
              )
              .filter((e) => e)
          ).join(", "),
      },
    },
    "nested.unitUnitType": {
      label: i18n.t("cb:RentalUnit.unitType", "Art"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(
          node.expand?.rentalUnits?.map((e) => e.data.unitType),
          "-",
          (value: string[]) =>
            _.uniq(
              value.map((e) =>
                LanguageService.translateLabel(
                  ObjectKindStruct.getUnitTypeBy(e)?.displayName
                )
              )
            ).join(", ")
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalUnit.unitType", "Art"),
        type: "string",
        selector: (node: RentalAgreement) =>
          _.uniq(
            node.expand?.rentalUnits
              ?.map((e) => e.data.unitType)
              ?.map((unitType) =>
                LanguageService.translateLabel(
                  ObjectKindStruct.getUnitTypeBy(unitType)?.displayName
                )
              )
              .filter((e) => e)
          ).join(", "),
      },
    },

    "data.balance": {
      label: i18n.t("cb:RentalAgreement.balance", "Kontostand"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      alignment: "right",
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(
          node?.data?.balance,
          "-",
          (value: number) => StringUtils.formatCurrency(value * -1),
          false,
          undefined,
          CSSUtils.getColorClassForNumber(node?.data?.balance * -1)
        ),
      renderFooter: (params) => (
        <div>
          <div className={`__font-size-sm`}>
            {renderCellValue(
              params?.aggregated?.data?.["positiveBalance"]?.balance,
              "-",
              (value: number) => StringUtils.formatCurrency(value * -1),
              false,
              undefined,
              CSSUtils.getColorClassForNumber(
                params?.aggregated?.data?.["positiveBalance"]?.balance * -1
              )
            )}
          </div>
          <div className={`__font-size-sm`}>
            {renderCellValue(
              params?.aggregated?.data?.["negativeBalance"]?.balance,
              "-",
              (value: number) => StringUtils.formatCurrency(value * -1),
              false,
              undefined,
              CSSUtils.getColorClassForNumber(
                params?.aggregated?.data?.["negativeBalance"]?.balance * -1
              )
            )}
          </div>
          {/* <div className={`__font-size-sm sum-balanace-row`}>
            {renderCellValue(
              params?.aggregated?.data?.["general"]?.balance,
              "-",
              (value: number) => StringUtils.formatCurrency(value * -1),
              true,
              undefined,
              CSSUtils.getColorClassForNumber(
                params?.aggregated?.data?.["general"]?.balance * -1
              )
            )}
          </div> */}
        </div>
      ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.balance", "Kontostand"),
        type: "number",
        totalFunction: "sum",
        style: {
          numFmt: getDefaultCurrencyNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return node?.data?.balance * -1;
        },
      },
    },
    "data.dunning.currentDunningActivity": {
      label: i18n.t("cb:RentalAgreement.currentDunningActivity", "Mahnlauf"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        node.data.dunning?.currentDunningActivtiy ? (
          <StructLoader
            structType="dunning"
            unitType={node.data.type}
            render={
              (struct: DunningStructClass) => (
                <AssetCell
                  assetType={AssetTypes.Activity.Dunning}
                  id={node.data.dunning?.currentDunningActivtiy}
                  render={(asset: APDunningActivity) =>
                    asset.data.dunning?.dunningLevel
                      ? LanguageService.translateLabel(
                          struct.getDunningLevel(
                            asset.data.dunning?.dunningLevel
                          )?.name
                        )
                      : asset.data.displayName
                  }
                />
              )
              // renderCellValue(node.data.dunning?.dunningLevel , "-", (value) => struct.getDunningLevel(value)?.name )
            }
          />
        ) : (
          "-"
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.currentDunningActivity", "Mahnlauf"),
        type: "string",
        selector: async (node: RentalAgreement) => {
          if (!node.data.dunning?.currentDunningActivtiy) {
            return "-";
          }
          const dunning = await CacheService.getData({
            oType: "asset",
            assetType: AssetTypes.Activity.Dunning,
            id: node.data.dunning?.currentDunningActivtiy,
            checkTables: true,
          });
          return dunning.data.dunning?.dunningLevel
            ? LanguageService.translateLabel(
                DunningStruct.getDunningLevel(
                  dunning.data.dunning?.dunningLevel
                )?.name
              )
            : dunning.data.displayName;
        },
      },
    },

    "data.stopDebitRun": {
      label: i18n.t(
        "cb:RentalAgreement.stopDebitRum",
        "Deaktivierte Sollstellungsautomatik"
      ),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <BFCheckbox checked={node.data.stopDebitRun} readOnly />
      ),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.stopDebitRum",
          "Deaktivierte Sollstellungsautomatik"
        ),
        type: "string",

        selector: (node: RentalAgreement) => {
          return node.data.stopDebitRun ? "TRUE" : "FALSE";
        },
      },
    },
    "data.dunning.stopDunningRun": {
      label: i18n.t("cb:RentalAgreement.stopDunningRun", "Mahnlauf gestoppt"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <BFCheckbox checked={node.data.dunning?.stopDunningRun} readOnly />
      ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.stopDunningRun", "Mahnlauf gestoppt"),
        type: "string",

        selector: (node: RentalAgreement) => {
          return node.data.dunning?.stopDunningRun ? "TRUE" : "FALSE";
        },
      },
    },

    "data.rentNet": {
      label: i18n.t("cb:RentalAgreement.rentNet", "NKM netto"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.rentNet, "-", (value: number) =>
          StringUtils.formatCurrency(value)
        ),
      renderFooter: (params) =>
        renderCellValue(
          params?.aggregated?.data?.["general"]?.rentNet,
          "-",
          (value: number) => StringUtils.formatCurrency(value)
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.rentNet", "NKM netto"),
        type: "number",
        totalFunction: "sum",
        style: {
          numFmt: getDefaultCurrencyNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return node?.data?.rentNet;
        },
      },
    },
    "data.rentGross": {
      label: i18n.t("cb:RentalAgreement.rentGross", "NKM brutto"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.rentGross, "-", (value: number) =>
          StringUtils.formatCurrency(value)
        ),
      renderFooter: (params) =>
        renderCellValue(
          params?.aggregated?.data?.["general"]?.rentGross,
          "-",
          (value: number) => StringUtils.formatCurrency(value)
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.rentGross", "NKM brutto"),
        type: "number",
        totalFunction: "sum",
        style: {
          numFmt: getDefaultCurrencyNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return node?.data?.rentGross;
        },
      },
    },
    "data.operatingCostNet": {
      label: i18n.t("cb:RentalAgreement.operatingCostNet", "Nebenkosten netto"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.operatingCostNet, "-", (value: number) =>
          StringUtils.formatCurrency(value)
        ),
      renderFooter: (params) =>
        renderCellValue(
          params?.aggregated?.data?.["general"]?.operatingCostNet,
          "-",
          (value: number) => StringUtils.formatCurrency(value)
        ),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.operatingCostNet",
          "Nebenkosten netto"
        ),
        type: "number",
        totalFunction: "sum",
        style: {
          numFmt: getDefaultCurrencyNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return node?.data?.operatingCostNet;
        },
      },
    },
    "data.operatingCostGross": {
      label: i18n.t(
        "cb:RentalAgreement.operatingCostGross",
        "Nebenkosten brutto"
      ),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.operatingCostGross, "-", (value: number) =>
          StringUtils.formatCurrency(value)
        ),
      renderFooter: (params) =>
        renderCellValue(
          params?.aggregated?.data?.["general"]?.operatingCostGross,
          "-",
          (value: number) => StringUtils.formatCurrency(value)
        ),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.operatingCostGross",
          "Nebenkosten brutto"
        ),
        type: "number",
        totalFunction: "sum",
        style: {
          numFmt: getDefaultCurrencyNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return node?.data?.operatingCostGross;
        },
      },
    },
    "data.deposit": {
      label: i18n.t("cb:RentalAgreement.deposit", "Kaution"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(
          _.sum((node?.data?.deposit || []).map((e) => e.depositValue)),
          "-",
          (value: number) => StringUtils.formatCurrency(value)
        ),
      renderFooter: (params) =>
        renderCellValue(
          params?.aggregated?.data?.["general"]?.deposit,
          "-",
          (value: number) => StringUtils.formatCurrency(value)
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.deposit", "Kaution"),
        type: "number",
        totalFunction: "sum",
        style: {
          numFmt: getDefaultCurrencyNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return _.sum((node?.data?.deposit || []).map((e) => e.depositValue));
        },
      },
    },
    "nested.unitArea": {
      label: i18n.t("cb:RentalAgreement.unitArea", "Fläche"),
      // sortKey: "",
      flexWidth: 120,
      sortable: false,
      resizable: true,
      render: (node: RentalAgreement, index, params) => {
        const areaSum = _.sum(
          node.expand?.rentalUnits?.map((e) => e.data.area || 0)
        );
        return renderCellValue(areaSum, "-", (value: number) =>
          StringUtils.formatArea(value)
        );
      },
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.unitArea", "Fläche"),
        type: "number",
        style: {
          numFmt: getDefaultAreaNumberFormat(),
        },
        selector: (node: RentalAgreement) => {
          return _.sum(node.expand?.rentalUnits?.map((e) => e.data.area || 0));
        },
      },
    },
    "nested.unitQuantity": {
      label: i18n.t("cb:RentalAgreement.unitQuantity", "Stellplätze/Sonstiges"),
      // sortKey: "",
      flexWidth: 120,
      sortable: false,
      resizable: true,
      render: (node: RentalAgreement, index, params) => {
        const quantity = _.sum(
          node.expand?.rentalUnits?.map((e) => e.data.quantity || 0)
        );
        return renderCellValue(quantity, "-");
      },
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.unitQuantity",
          "Stellplätze/Sonstiges"
        ),
        type: "number",
        selector: (node: RentalAgreement) => {
          return _.sum(
            node.expand?.rentalUnits?.map((e) => e.data.quantity || 0)
          );
        },
      },
    },
    "data.moveIn": {
      label: i18n.t("cb:RentalAgreement.moveIn", "Einzug"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.moveIn, "-", (value: Date) =>
          moment(value).format(i18n.t("Formats.dateFormat"))
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.moveIn", "Einzug"),
        type: "date",
        selector: (node: RentalAgreement) => {
          return node?.data?.moveIn ? new Date(node?.data?.moveIn) : null;
        },
      },
    },
    "data.agreementExpiration": {
      label: i18n.t("cb:RentalAgreement.agreementExpiration", "Vertrag bis"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.agreementExpiration, "-", (value: Date) =>
          moment(value).format(i18n.t("Formats.dateFormat"))
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.agreementExpiration", "Vertrag bis"),
        type: "date",
        selector: (node: RentalAgreement) => {
          return node?.data?.agreementExpiration
            ? new Date(node?.data?.agreementExpiration)
            : null;
        },
      },
    },
    "data.moveOut": {
      label: i18n.t("cb:RentalAgreement.moveOut", "Auszug"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.moveOut, "-", (value: Date) =>
          moment(value).format(i18n.t("Formats.dateFormat"))
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.moveOut", "Auszug"),
        type: "date",
        selector: (node: RentalAgreement) => {
          return node?.data?.moveOut ? new Date(node?.data?.moveOut) : null;
        },
      },
    },
    "data.currentState": {
      label: i18n.t("cb:RentalAgreement.currentState", "Aktueller Status"),
      // sortKey: "",
      flexWidth: 220,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data?.currentState, "-"),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.currentState", "Aktueller Status"),
        type: "string",
        selector: (node: RentalAgreement) => {
          return node?.data?.currentState;
        },
      },
    },

    "data.automatic.monthBeforeContractEnd": {
      label: i18n.t(
        "cb:RentalAgreement.automaticMonthBeforeContractEnd",
        "Auszugsautomatik vor Auszug"
      ),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data.automatic?.monthBeforeContractEnd, "-"),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.automaticMonthBeforeContractEnd",
          "Auszugsautomatik vor Auszug"
        ),
        type: "number",
        selector: (node: RentalAgreement) => {
          return node?.data.automatic?.monthBeforeContractEnd;
        },
      },
    },

    "data.automatic.extendByMonths": {
      label: i18n.t(
        "cb:RentalAgreement.automaticExtendByMonths",
        "Auszugsautomatik Verlängerung"
      ),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node?.data.automatic?.extendByMonths, "-"),
      export: {
        width: 20,
        label: i18n.t(
          "cb:RentalAgreement.automaticExtendByMonths",
          "Auszugsautomatik Verlängerung"
        ),
        type: "number",
        selector: (node: RentalAgreement) => {
          return node?.data.automatic?.extendByMonths;
        },
      },
    },
    "data.graduatedRent": {
      label: i18n.t("cb:RentalAgreement.graduatedRent", "Staffelmiete"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <BFCheckbox checked={node.data.graduatedRent?.length > 0} readOnly />
      ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.graduatedRent", "Staffelmiete"),
        type: "string",
        selector: (node: RentalAgreement) => {
          return node.data.graduatedRent?.length > 0 ? "TRUE" : "FALSE";
        },
      },
    },
    "data.index.type": {
      label: i18n.t("cb:RentalAgreement.indexType", "Indexierung"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node.data.index?.type, "-", (value) =>
          value === "yearly"
            ? i18n.t("cb:RentalAgreement.Form.Index.yearlyType", "Jährlich")
            : i18n.t("cb:RentalAgreement.Form.Index.defaultType", "Normal")
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.indexType", "Indexierung"),
        type: "string",
        selector: (node: RentalAgreement) => {
          return node.data.index?.type
            ? node.data.index?.type === "yearly"
              ? i18n.t("cb:RentalAgreement.Form.Index.yearlyType", "Jährlich")
              : i18n.t("cb:RentalAgreement.Form.Index.defaultType", "Normal")
            : "-";
        },
      },
    },
    "data.index.indexFactor": {
      label: i18n.t("cb:RentalAgreement.indexFactor", "Indexfaktor"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue((node.data.index as any)?.indexFactor, "-", (value) =>
          StringUtils.formatPercent(value)
        ),
      export: {
        width: 20,
        label: i18n.t("cb:RentalAgreement.indexFactor", "Indexfaktor"),
        type: "number",
        selector: (node: RentalAgreement) => {
          return (node.data.index as any)?.indexFactor;
        },
      },
    },
    "data.index.indexMonth": {
      label: i18n.t("cb:RentalAgreement.indexMonth", "Stichmonat"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(
          (node.data.index as any)?.indexMonth,
          "-",
          (value: string) => moment().month(value).format("MMMM")
        ),
      export: {
        width: 80,
        label: i18n.t("cb:RentalAgreement.indexMonth", "Stichmonat"),
        type: "string",
        selector: (node: RentalAgreement) => {
          return (node.data.index as any)?.indexMonth
            ? moment()
                .month((node.data.index as any)?.indexMonth)
                .format("MMMM")
            : null;
        },
      },
    },

    "data.index.lastPriceIndex": {
      label: i18n.t("cb:RentalAgreement.lastPriceIndex", "Letzter Index"),
      // sortKey: "",
      flexWidth: 120,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node.data.index?.lastPriceIndex, "-"),
      export: {
        width: 60,
        label: i18n.t("cb:RentalAgreement.lastPriceIndex", "Letzter Index"),
        type: "number",
        selector: (node: RentalAgreement) => {
          return node.data.index?.lastPriceIndex;
        },
      },
    },
    "data.index.lastPriceIndexChange": {
      label: i18n.t(
        "cb:RentalAgreement.lastPriceIndexChange",
        "Letzte Änderung"
      ),
      // sortKey: "",
      flexWidth: 80,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node.data.index?.lastPriceIndex, "-"),
      export: {
        width: 60,
        label: i18n.t(
          "cb:RentalAgreement.lastPriceIndexChange",
          "Letzte Änderung"
        ),
        type: "date",
        selector: (node: RentalAgreement) => {
          return node.data.index?.lastPriceIndexChange;
        },
      },
    },
    "data.index.rentChangeFactor": {
      label: i18n.t(
        "cb:RentalAgreement.rentChangeFactor",
        "Mietanpassungsfaktor"
      ),
      // sortKey: "",
      flexWidth: 80,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) =>
        renderCellValue(node.data.index?.rentChangeFactor, "-", (value) =>
          StringUtils.formatPercent(value)
        ),
      export: {
        width: 60,
        label: i18n.t(
          "cb:RentalAgreement.rentChangeFactor",
          "Mietanpassungsfaktor"
        ),
        type: "number",
        selector: (node: RentalAgreement) => {
          return node.data.index?.rentChangeFactor;
        },
      },
    },
    "data.index.backPayPossible": {
      label: i18n.t(
        "cb:RentalAgreement.backPayPossible",
        "Nachzahlung möglich"
      ),
      // sortKey: "",
      flexWidth: 80,
      sortable: true,
      resizable: true,
      render: (node: RentalAgreement, index, params) => (
        <BFCheckbox checked={node.data.index?.backPayPossible} readOnly />
      ),
      export: {
        width: 60,
        label: i18n.t(
          "cb:RentalAgreement.backPayPossible",
          "Nachzahlung möglich"
        ),
        type: "string",
        selector: (node: RentalAgreement) => {
          return node.data.index?.backPayPossible ? "TRUE" : "FALSE";
        },
      },
    },

    // "data.option": {
    //   label: i18n.t("cb:RentalAgreement.option", "Option aktiv"),
    //   // sortKey: "",
    //   flexWidth: 80,
    //   sortable: true,
    //   resizable: true,
    //   render: (node: RentalAgreement, index, params) => (
    //     <BFCheckbox checked={hasValue(node.data.option)} readOnly />
    //   ),
    //   export: {
    //     width: 60,
    //     label: i18n.t("cb:RentalAgreement.option", "Option aktiv"),
    //     type: "string",
    //     selector: (node: RentalAgreement) => {
    //       return hasValue(node.data.option) ? "TRUE" : "FALSE";
    //     },
    //   },
    // },
    // "data.index.optionsUsed": {
    //   label: i18n.t("cb:RentalAgreement.optionsUsed", "Optionen genutzt"),
    //   // sortKey: "",
    //   flexWidth: 120,
    //   sortable: true,
    //   resizable: true,
    //   render: (node: RentalAgreement, index, params) =>
    //     renderCellValue(node.data.option?.optionsUsed, "-"),
    //   export: {
    //     width: 60,
    //     label: i18n.t("cb:RentalAgreement.optionsUsed", "Optionen genutzt"),
    //     type: "number",
    //     selector: (node: RentalAgreement) => {
    //       return node.data.option?.optionsUsed;
    //     },
    //   },
    // },
    // "data.index.optionsCount": {
    //   label: i18n.t("cb:RentalAgreement.optionsCount", "Anzahl Optionen"),
    //   // sortKey: "",
    //   flexWidth: 120,
    //   sortable: true,
    //   resizable: true,
    //   render: (node: RentalAgreement, index, params) =>
    //     renderCellValue(node.data.option?.optionsCount, "-"),
    //   export: {
    //     width: 60,
    //     label: i18n.t("cb:RentalAgreement.optionsCount", "Anzahl Optionen"),
    //     type: "number",
    //     selector: (node: RentalAgreement) => {
    //       return node.data.option?.optionsCount;
    //     },
    //   },
    // },

    // "data.option.optionUntil": {
    //   label: i18n.t("cb:RentalAgreement.option", "Option bis"),
    //   // sortKey: "",
    //   flexWidth: 80,
    //   sortable: true,
    //   resizable: true,
    //   render: (node: RentalAgreement, index, params) =>
    //     renderCellValue(
    //       node.data.option?.optionUntil,
    //       "-",
    //       StringUtils.formatDate
    //     ),
    //   export: {
    //     width: 60,
    //     label: i18n.t("cb:RentalAgreement.option", "Option bis"),
    //     type: "date",
    //     selector: (node: RentalAgreement) => {
    //       return node.data.option?.optionUntil;
    //     },
    //   },
    // },
    // "data.option.optionBy": {
    //   label: i18n.t("cb:RentalAgreement.optionBy", "Option von"),
    //   // sortKey: "",
    //   flexWidth: 80,
    //   sortable: true,
    //   resizable: true,
    //   render: (node: RentalAgreement, index, params) =>
    //     renderCellValue(
    //       node.data.option?.optionBy,
    //       "-",
    //       (value) =>
    //         RentalAgreementOptionBy().find((e) => e.value === value)?.label
    //     ),
    //   export: {
    //     width: 60,
    //     label: i18n.t("cb:RentalAgreement.optionBy", "Option von"),
    //     type: "string",
    //     selector: (node: RentalAgreement) => {
    //       return node.data.option?.optionBy
    //         ? RentalAgreementOptionBy().find(
    //             (e) => e.value === node.data.option?.optionBy
    //           )?.label
    //         : "-";
    //     },
    //   },
    // },

    // "data.index.extendsByMonths": {
    //   label: i18n.t(
    //     "cb:RentalAgreement.optionExtendsByMonths",
    //     "Option verlängert um"
    //   ),
    //   // sortKey: "",
    //   flexWidth: 120,
    //   sortable: true,
    //   resizable: true,
    //   render: (node: RentalAgreement, index, params) =>
    //     renderCellValue(node.data.option?.extendsByMonths, "-"),
    //   export: {
    //     width: 60,
    //     label: i18n.t(
    //       "cb:RentalAgreement.optionExtendsByMonths",
    //       "Option verlängert um"
    //     ),
    //     type: "number",
    //     selector: (node: RentalAgreement) => {
    //       return node.data.option?.extendsByMonths;
    //     },
    //   },
    // },

    documentUnmetRequirements: {
      label: i18n.t(
        "cb:RentalAgreement.UnmetRequirements",
        "Fehlende Dokumente"
      ),
      flexWidth: 120,
      render: (node: RentalAgreement, key, columnConf) => {
        const assetParams: DocumentStoreAssetParams = {
          asset: node,
          type: node.data.type,
          assetType: AssetTypes.Rental.RentalAgreement,
          documentsFieldPath: "data.attachments",
        };
        const dsDocumentStoreDirectory =
          DSService.getDirectoryConfigurationForAsset(
            node.data.type,
            AssetTypes.Rental.RentalAgreement,
            "data.attachments"
          );
        const flattenDirectoryMap = DSService.getFlattenDirectoryMap(
          dsDocumentStoreDirectory
        );
        return renderCellValue(
          <DSUnmentRequirementsColumn
            onClick={(node: RentalAgreement) => {
              DataBus.emit(DataBusSubKeys.ROUTE, {
                append: true,
                route: `${node._id}/attachments`,
              });
            }}
            assetParams={assetParams}
            flattenDirectoryMap={flattenDirectoryMap}
            directory={dsDocumentStoreDirectory}
          />
        );
      },
    },
  } as { [key: string]: ColumnConfig });
